import type { WikiItemStatFields } from "~~/types/gamedata";
import type { ShipexkursIconName } from "~/utils/shipexkurs-icons";

export interface ComponentStatDetail {
  label: string;
  value: string;
}

/** Kleines Icon+Wert-Badge für ressourcennetz-gekoppelte Kennzahlen (z.B. Kühlbedarf eines Power Plants). */
export interface ComponentStatBadge {
  icon: ShipexkursIconName;
  value: string;
}

export interface ComponentStats {
  /** Große, hervorgehobene Kennzahl (z.B. "1091" + "Burst DPS"). */
  headline: { value: string; unit: string } | null;
  /** Kleine Detailzeile darunter (z.B. "Feuerrate 750 shots/min · Reichweite 1924 m"). */
  details: ComponentStatDetail[];
  /** Zusätzliche Icon+Wert-Badges neben der Headline (z.B. Kühlbedarf bei Power Plants). */
  badges?: ComponentStatBadge[];
}

const EMPTY: ComponentStats = { headline: null, details: [], badges: [] };

/** Deutsche Zahlenformatierung inline statt über das auto-importierte `formatCurrency`
 * — ein expliziter Cross-Util-Import (`~/utils/format-currency`) bricht beim
 * SSR-Rendern (vite-node kann den Alias aus einer anderen `app/utils`-Datei
 * heraus nicht auflösen, nur wenn er in eine `.vue`-Komponente einkompiliert
 * wird), live geprüft. */
function n(value: number | null | undefined, decimals = 0): string | null {
  if (value == null) return null;
  return value.toLocaleString("de-DE", { maximumFractionDigits: decimals });
}

function pct(value: number | null | undefined): string | null {
  if (value == null) return null;
  return `${Math.round(value * 100)}%`;
}

function detail(label: string, value: string | null | undefined, unit = ""): ComponentStatDetail | null {
  if (value == null) return null;
  return { label, value: unit ? `${value} ${unit}` : value };
}

function countermeasureStats(item: WikiItemStatFields): ComponentStats {
  const ammo = n(item.vehicle_weapon?.ammunition?.capacity);
  const headline = ammo ? { value: ammo, unit: "Munition" } : null;

  const details = [
    detail("Feuerrate", n(item.vehicle_weapon?.rpm), "shots/min"),
    detail("Geschwindigkeit", n(item.vehicle_weapon?.ammunition?.speed), "m/s"),
    detail("Reichweite", n(item.vehicle_weapon?.ammunition?.range), "m"),
  ].filter((row): row is ComponentStatDetail => !!row);

  return { headline, details };
}

function weaponStats(item: WikiItemStatFields): ComponentStats {
  const weapon = item.vehicle_weapon;
  if (!weapon) return EMPTY;

  const burst = n(weapon.damage?.burst);
  const headline = burst ? { value: burst, unit: "Burst DPS" } : null;

  const efficiency = weapon.damage?.sustained_60s && weapon.damage?.burst
    ? Math.round((weapon.damage.sustained_60s / weapon.damage.burst) * 100)
    : null;

  const details = [
    detail("Effizienz", efficiency != null ? `${efficiency}%` : null),
    detail("Durchschlag", n(weapon.ammunition?.penetration?.base_distance, 2), "m"),
    detail("Feuerrate", n(weapon.rpm), "shots/min"),
    detail("Energie", n(weapon.damage_per_shot), "DMG"),
    detail("Geschwindigkeit", n(weapon.ammunition?.speed), "m/s"),
    detail("Reichweite", n(weapon.range), "m"),
    detail("Energiepool", n(weapon.capacitor?.max_ammo_load)),
    detail("Max. Regen/s", n(weapon.capacitor?.regen_per_second)),
    detail("Energie/Schuss", n(weapon.capacitor?.costs_per_shot)),
  ].filter((row): row is ComponentStatDetail => !!row);

  return { headline, details };
}

function shieldStats(item: WikiItemStatFields): ComponentStats {
  const shield = item.shield;
  if (!shield) return EMPTY;

  const hp = n(shield.max_health);
  const headline = hp ? { value: hp, unit: "HP" } : null;

  const details = [
    detail("Regen-Zeit", n(shield.regen_time, 1), "s"),
    detail("Ausfall-Verzögerung", n(shield.regen_delay?.downed), "s"),
    detail("Schadens-Verzögerung", n(shield.regen_delay?.damage), "s"),
    detail("Phys. Res.", pct(shield.resistance?.physical?.max)),
    detail("Dist.-Res.", pct(shield.resistance?.distortion?.max)),
  ].filter((row): row is ComponentStatDetail => !!row);

  return { headline, details };
}

function radarStats(item: WikiItemStatFields): ComponentStats {
  const radar = item.radar;
  if (!radar) return EMPTY;

  const range = n(radar.aim_assist?.distance_max_assignment);
  const headline = range ? { value: `${range} m`, unit: "Zielhilfe-Reichweite" } : null;

  const details = [
    detail("IR", pct(radar.sensitivity?.infrared)),
    detail("EM", pct(radar.sensitivity?.electromagnetic)),
    detail("CS", pct(radar.sensitivity?.cross_section)),
    detail("RS", pct(radar.sensitivity?.resource)),
  ].filter((row): row is ComponentStatDetail => !!row);

  return { headline, details };
}

function armorStats(item: WikiItemStatFields): ComponentStats {
  const armor = item.armor;
  if (!armor) return EMPTY;

  const hp = n(armor.health);
  const headline = hp ? { value: hp, unit: "HP" } : null;

  const details = [
    detail("Physisch", pct(armor.damage_multiplier?.physical)),
    detail("Energie", pct(armor.damage_multiplier?.energy)),
    detail("Phys.-Abl.", n(armor.deflection?.physical)),
    detail("Energie-Abl.", n(armor.deflection?.energy)),
    detail("Eigen-Res. Phys.", pct(armor.resistance_multiplier?.physical)),
    detail("Eigen-Res. Energie", pct(armor.resistance_multiplier?.energy)),
  ].filter((row): row is ComponentStatDetail => !!row);

  return { headline, details };
}

function quantumInterdictionStats(item: WikiItemStatFields): ComponentStats {
  const qed = item.quantum_interdiction_generator;
  if (!qed) return EMPTY;

  const radiusKm = qed.pulse?.radius != null ? n(qed.pulse.radius / 1000, 1) : null;
  const headline = radiusKm ? { value: radiusKm, unit: "Snare-Reichweite (km)" } : null;

  const details = [
    detail("Jammer-Reichweite", qed.jamming?.range != null ? n(qed.jamming.range / 1000, 1) : null, "km"),
    detail("Aufladezeit", n(qed.pulse?.charge_time, 1), "s"),
    detail("Entladezeit", n(qed.pulse?.discharge_time, 1), "s"),
    detail("Abklingzeit", n(qed.pulse?.cooldown_time, 1), "s"),
  ].filter((row): row is ComponentStatDetail => !!row);

  return { headline, details };
}

function empStats(item: WikiItemStatFields): ComponentStats {
  const emp = item.emp;
  if (!emp) return EMPTY;

  const damage = n(emp.distortion_damage);
  const headline = damage ? { value: damage, unit: "Verzerrungsschaden" } : null;

  const details = [
    detail("Radius", n(emp.emp_radius), "m"),
    detail("Kernradius", n(emp.min_emp_radius), "m"),
    detail("Aufladezeit", n(emp.charge_duration, 1), "s"),
    detail("Entladezeit", n(emp.unleash_duration, 2), "s"),
    detail("Abklingzeit", n(emp.cooldown_duration, 1), "s"),
  ].filter((row): row is ComponentStatDetail => !!row);

  return { headline, details };
}

function quantumDriveStats(item: WikiItemStatFields): ComponentStats {
  const drive = item.quantum_drive;
  if (!drive) return EMPTY;

  const speed = n(drive.standard_jump?.drive_speed);
  const headline = speed ? { value: `${speed} m/s`, unit: "Geschwindigkeit" } : null;

  const details = [
    detail("Reichweite", drive.jump_range_formatted ?? null),
    detail("Verbrauch", n(drive.fuel_consumption_scu_per_gm, 2), "mSCU/Gm"),
    detail("Spool-Up", n(drive.standard_jump?.spool_up_time, 1), "s"),
    detail("Abklingzeit", n(drive.standard_jump?.cooldown_time, 1), "s"),
  ].filter((row): row is ComponentStatDetail => !!row);

  return { headline, details };
}

const SIGNAL_TYPE_LABELS: Record<string, string> = {
  CrossSection: "Optisch",
  Infrared: "Infrarot",
  Electromagnetic: "Elektromagnetisch",
};

function missileStats(item: WikiItemStatFields): ComponentStats {
  const missile = item.missile;
  if (!missile) return EMPTY;

  const damage = n(missile.damage_total);
  const headline = damage ? { value: damage, unit: "DMG" } : null;

  const details = [
    detail("Geschwindigkeit", n(missile.flight?.speed), "m/s"),
    detail("Reichweite", n(missile.flight?.range), "m"),
    detail("Erfassungsreichweite", n(missile.target_lock?.range_max), "m"),
    detail("Erfassungszeit", n(missile.lock_time, 2), "s"),
    detail("Tracking", missile.signal_type ? (SIGNAL_TYPE_LABELS[missile.signal_type] ?? missile.signal_type) : null),
  ].filter((row): row is ComponentStatDetail => !!row);

  return { headline, details };
}

function fuelTankStats(item: WikiItemStatFields): ComponentStats {
  const tank = item.fuel_tank;
  if (!tank?.capacity) return EMPTY;

  const scu = n(tank.capacity, 1);
  const headline = scu ? { value: scu, unit: "SCU" } : null;

  // Externe Betankungs-Container (`ExternalFuelTank`, z.B. Starfarer-Fuel-Pods)
  // sind generische Behälter ohne festen Treibstofftyp — anders als interne
  // Tanks, die fest als Hydrogen- oder Quantentank verbaut sind.
  const details = [
    item.type === "ExternalFuelTank"
      ? null
      : detail("Treibstoffart", item.type === "QuantumFuelTank" ? "Quantum" : "Hydrogen"),
  ].filter((row): row is ComponentStatDetail => !!row);

  return { headline, details };
}

function fuelIntakeStats(item: WikiItemStatFields): ComponentStats {
  const intake = item.fuel_intake;
  if (!intake?.fuel_push_rate) return EMPTY;

  const rate = n(intake.fuel_push_rate, 2);
  const headline = rate ? { value: rate, unit: "Scoop-Rate" } : null;

  return { headline, details: [] };
}

// Betankungs-Nozzle (z.B. Starfarer-Refuel-Arm): kein eigener Item-Typ, die
// Transferrate steckt im generischen `resource_network` als `Consumption`-
// Delta je Ressource (Fuel = Hydrogen, QuantumFuel = Quantum).
const FUEL_TRANSFER_RESOURCE_LABELS: Record<string, string> = {
  Fuel: "H₂",
  QuantumFuel: "Qt",
};

function fuelTransferStats(item: WikiItemStatFields): ComponentStats {
  const deltas = item.resource_network?.states?.[0]?.deltas ?? [];
  const rates = deltas
    .filter((delta) => delta.type === "Consumption" && delta.resource && FUEL_TRANSFER_RESOURCE_LABELS[delta.resource] && delta.rate != null)
    .map((delta) => `${n(delta.rate, 2)} ${FUEL_TRANSFER_RESOURCE_LABELS[delta.resource!]}`);

  if (!rates.length) return EMPTY;

  return { headline: { value: rates.join(" / "), unit: "Fuel Rate" }, details: [] };
}

function cargoGridStats(item: WikiItemStatFields): ComponentStats {
  const grid = item.cargo_grid;
  if (!grid) return EMPTY;

  const scu = n(grid.scu);
  const headline = scu ? { value: `${scu} SCU`, unit: "Kapazität" } : null;

  const details = [
    detail("Max. Containergröße", n(grid.max_scu_box), "SCU"),
  ].filter((row): row is ComponentStatDetail => !!row);

  return { headline, details };
}

function coolerStats(item: WikiItemStatFields): ComponentStats {
  const cooler = item.cooler;
  // `cooling_rate` ist von der API als deprecated markiert und liefert
  // live durchgängig `null` (verifiziert gegen ~75 Kühler) —
  // `coolant_segment_generation` ist die tatsächlich befüllte Kennzahl.
  const coolant = cooler?.coolant_segment_generation;
  if (coolant == null) return EMPTY;

  const headline = { value: n(coolant) ?? "", unit: "Kühlung" };

  const details = [
    detail("IR-Unterdrückung", pct(cooler?.suppression_ir_factor)),
    detail("Wärme-Unterdrückung", pct(cooler?.suppression_heat_factor)),
  ].filter((row): row is ComponentStatDetail => !!row);

  // Kühler ziehen selbst Energie aus dem Netz, um ihr Kühlmittel zu erzeugen
  // — analoges Badge zum Kühlbedarf bei `powerPlantStats()`.
  const powerUsage = item.resource_network?.usage?.power?.max;
  const badges: ComponentStatBadge[] = powerUsage != null
    ? [{ icon: "powerplant", value: n(powerUsage) ?? "" }]
    : [];

  return { headline, details, badges };
}

function powerPlantStats(item: WikiItemStatFields): ComponentStats {
  const powerPlant = item.power_plant;
  // `power_output` ist von der API als deprecated markiert und liefert live
  // durchgängig `null` (verifiziert gegen ~80 Power Plants) —
  // `power_segment_generation` ist die tatsächlich befüllte Kennzahl.
  const power = powerPlant?.power_segment_generation;
  if (power == null) return EMPTY;

  const headline = { value: n(power) ?? "", unit: "Energie" };

  // Kühlbedarf steckt nicht am `power_plant`-Objekt, sondern generisch am
  // `resource_network` — ein Power Plant "verbraucht" Kühlmittel-Segmente in
  // exakt der Höhe seiner erzeugten Energie-Segmente (verifiziert gegen die
  // Live-API: `usage.coolant.max` === `power_segment_generation`).
  const coolantUsage = item.resource_network?.usage?.coolant?.max;
  const badges: ComponentStatBadge[] = coolantUsage != null
    ? [{ icon: "cooler", value: n(coolantUsage) ?? "" }]
    : [];

  return { headline, details: [], badges };
}

/** Kraftwerte kommen von der API in Newton (verifiziert: "Durus Tractor Beam" `force.max: 500000`) — für die Anzeige in Kilonewton umgerechnet. */
function kiloNewton(value: number | null | undefined): string | null {
  return value == null ? null : n(value / 1000, 1);
}

function tractorBeamStats(item: WikiItemStatFields): ComponentStats {
  const tractorBeam = item.tractor_beam;
  if (!tractorBeam) return EMPTY;

  const maxForce = kiloNewton(tractorBeam.force?.max);
  const headline = maxForce ? { value: `${maxForce} kN`, unit: "Zugkraft" } : null;

  const details = [
    detail("Reichweite", n(tractorBeam.range?.max), "m"),
    detail("Winkel", n(tractorBeam.range?.max_angle), "°"),
  ].filter((row): row is ComponentStatDetail => !!row);

  return { headline, details };
}

function miningLaserStats(item: WikiItemStatFields): ComponentStats {
  const miningLaser = item.mining_laser;
  if (!miningLaser) return EMPTY;

  const power = n(miningLaser.power_transfer ?? miningLaser.laser_power?.max);
  return {
    headline: power ? { value: `${power} PWR/s`, unit: "Leistung" } : null,
    details: [],
  };
}

/** `SalvageHead`/`SalvageModifier`/`SalvageFieldEmitter` — Salvage-Werkzeuge haben keinen eigenen Item-Typ, ihre Kennzahlen stecken im generischen `vehicle_weapon`/`weapon_modifier`. */
const SALVAGE_TYPES = new Set(["SalvageHead", "SalvageModifier", "SalvageFieldEmitter"]);

function salvageStats(item: WikiItemStatFields): ComponentStats {
  // Salvage-Heads und Field-Emitter bleiben reine Strukturzeilen. Nur die
  // austauschbaren Scraper-Module tragen die gewünschten Vergleichswerte.
  if (item.type !== "SalvageModifier") return EMPTY;
  // Tractor-Aufsätze werden als SalvageModifier geliefert, enthalten in
  // unserer Quelle aber keine belastbaren Kraft-/Reichweitenwerte. Die
  // neutralen 1×-Salvage-Multiplikatoren wären dafür irreführend.
  if (item.sub_type === "SalvageModifier_TractorBeam") return EMPTY;

  // Salvage-Module (`SalvageModifier`, z.B. "Cinch Scraper Module"): reine
  // Multiplikator-Aufsätze ohne eigene `vehicle_weapon`-Daten, stecken in
  // `weapon_modifier.salvage`.
  const modifier = item.weapon_modifier?.salvage;
  if (modifier) {
    const speed = modifier.salvage_speed_multiplier != null ? `${n(modifier.salvage_speed_multiplier, 2)}×` : null;
    const headline = speed ? { value: speed, unit: "Geschwindigkeit" } : null;

    const details = [
      // `radius_multiplier` ist trotz des Namens kein Multiplikator, sondern
      // bereits der reale Beam-Radius in Metern (verifiziert am Cinch Scraper
      // Module: `radius_multiplier: 1.5` deckt sich mit "Rayon: 1.5m"/"半径：1.5米"
      // in der FR-/ZH-Beschreibung — nur die DE-Beschreibung nennt den Wert nicht).
      detail("Radius", modifier.radius_multiplier != null ? n(modifier.radius_multiplier, 2) : null, "m"),
      detail("Effizienz", modifier.extraction_efficiency != null ? `${n(modifier.extraction_efficiency, 2)}×` : null),
    ].filter((row): row is ComponentStatDetail => !!row);

    return { headline, details };
  }

  return EMPTY;
}

function resourceContainerStats(item: WikiItemStatFields): ComponentStats {
  const capacity = n(item.resource_container?.capacity?.scu);
  return {
    headline: capacity ? { value: `${capacity} SCU`, unit: "Kapazität" } : null,
    details: [],
  };
}

function jumpDriveStats(item: WikiItemStatFields): ComponentStats {
  const drive = item.jump_drive;
  if (!drive) return EMPTY;

  const efficiency = n(drive.fuel_usage_efficiency_multiplier, 2);
  const headline = efficiency ? { value: `x${efficiency}`, unit: "Treibstoffeffizienz" } : null;

  const details = [
    detail("Ausrichtungsrate", n(drive.alignment_rate, 2), "°/s"),
    detail("Abstimmungsrate", n(drive.tuning_rate, 2), "°/s"),
    detail("Ausrichtungsverfall", n(drive.alignment_decay_rate, 2), "°/s"),
    detail("Abstimmungsverfall", n(drive.tuning_decay_rate, 2), "°/s"),
  ].filter((row): row is ComponentStatDetail => !!row);

  return { headline, details };
}

/**
 * Baut aus dem typ-spezifischen Stats-Objekt eines Items (`vehicle_weapon`,
 * `shield`, `cooler`, ...) die Anzeige-Kennzahlen für den ShipExkurs-
 * Komponentenbaum: eine hervorgehobene Headline-Zahl plus eine Detailzeile.
 * `EMPTY`, wenn der Item-Typ keine bekannten Stats trägt (z.B. Lebenserhaltung).
 *
 * Nimmt bewusst `WikiItemStatFields` statt `WikiItem` entgegen: die Stats
 * stecken oft schon direkt am `equipped_item` eines Ports (kein
 * `WikiItem.uuid`/`.slug` nötig) — siehe `WikiVehiclePort.equipped_item`.
 */
export function getComponentStats(item: WikiItemStatFields | null | undefined): ComponentStats {
  if (!item) return EMPTY;

  // Gegenmaßnahmen, Tractor Beams, Mining Laser und Salvage-Werkzeuge tragen
  // zusätzlich ein generisches `vehicle_weapon`-Objekt (Feuerrate/Munition/
  // Geschwindigkeit) — für sie sind aber die spezifischen Felder relevant,
  // deshalb vor der generischen Waffen-Prüfung behandeln (sonst zeigt z.B.
  // ein Tractor Beam eine irrelevante Munitionsgeschwindigkeit statt Zugkraft).
  if (item.counter_measure) return countermeasureStats(item);
  if (item.missile) return missileStats(item);
  if (item.tractor_beam) return tractorBeamStats(item);
  if (item.mining_laser) return miningLaserStats(item);
  if (item.type && SALVAGE_TYPES.has(item.type)) return salvageStats(item);
  if (item.vehicle_weapon) return weaponStats(item);
  if (item.shield) return shieldStats(item);
  if (item.radar) return radarStats(item);
  if (item.armor) return armorStats(item);
  if (item.quantum_interdiction_generator) return quantumInterdictionStats(item);
  if (item.emp) return empStats(item);
  if (item.quantum_drive) return quantumDriveStats(item);
  if (item.jump_drive) return jumpDriveStats(item);
  if (item.cargo_grid) return cargoGridStats(item);
  if (item.resource_container) return resourceContainerStats(item);
  if (item.fuel_tank) return fuelTankStats(item);
  if (item.fuel_intake) return fuelIntakeStats(item);
  if (item.type === "DockingCollar" && item.sub_type === "Fuel") return fuelTransferStats(item);
  if (item.cooler) return coolerStats(item);
  if (item.power_plant) return powerPlantStats(item);

  if (item.thruster?.thrust_capacity) {
    // Wie bei Tractor Beams liefert die API Schubkraft in Newton, nicht kN
    // (verifiziert: der bisherige unkonvertierte Wert lag im Millionenbereich).
    return { headline: { value: `${kiloNewton(item.thruster.thrust_capacity)} kN`, unit: "Schub" }, details: [] };
  }

  return EMPTY;
}
